/**
 * @description 路演渠道 Bridge：iframe 嵌入页 `/thirdparty/externalAiChatIframe` 的 customAiChat 初始 config。
 * 业务 ID 由 URL query：`businessId`（可选 `sourceType`，缺省为路演 ROADSHOW）。
 *
 * 路演场景下「会议原文 + 会议文档」知识库选项由 {@link fetchRoadshowEmbedKnowledgeBaseOptions} 异步拉取，
 * 在 `chatExternalIframe.vue` 中在 `businessId` 就绪后合并进 `config`（与 portal `chat-home.requestDocuments` 对齐）。
 */
import { isThirdPartyMode, BridgeBase } from './bridge_utils';
import { AI_CHAT_SOURCE_TYPE, ROADSHOW_FILE_EMBEDDING_STATUS } from '@/constants';
import { getSummaryInfo, noticepagedocument, attachmentlist, getRoadshowDocVectorInfo } from '@/jmHttp/api/summary';
import { checkDocChunk, uploadDocChunk } from '@/jmHttp/api/aiChat';

const BRM_CHANNEL = 'roadshow';

export const isThirdParty = isThirdPartyMode({ brmChannel: BRM_CHANNEL, noComeinChannel: true });

if (isThirdParty) window.comeinChannel = 'roadshow';

/**
 * 合并路演「相关文档」与「播放文档」列表（与 portal chat-home.mergeArrays 一致）
 */
function mergeRoadshowDocArrays(docList = [], liveDocList = []) {
  const normalizedLive = liveDocList.map((item) => ({
    ...item,
    docname: item.filename,
    docsize: item.filesize,
    docpath: item.attachment
  }));
  return normalizedLive.concat(
    docList.filter((item) => !normalizedLive.find((sub) => item.docname == sub.docname && item.docsize == sub.docsize))
  );
}

async function getRoadshowDocumentsVectorInfo(docArr) {
  const payload = docArr.map((doc) => ({
    id: doc.id,
    type: doc.vectorType
  }));
  const res = await getRoadshowDocVectorInfo(payload);
  if (res.data?.length) {
    return docArr.map((doc) => {
      const docVectorInfo = res.data.find((item) => String(item.id) === String(doc.id)) || {};
      return {
        ...doc,
        id: docVectorInfo.vectorlibId,
        embeddingStatus: docVectorInfo.status
      };
    });
  }
  return [];
}

/**
 * 路演 iframe：构建 knowledgeBaseOptions（会议原文 useSummary + 会议文档 docBusinessIds），
 * 并在未同步 RAG 时触发纪要 uploadDocChunk（与 portal chat-home.requestDocuments 一致）。
 *
 * @param {{ roadshowId: string, title?: string, t: (key: string) => string }} params
 * @returns {Promise<{ knowledgeBaseOptions: object[], meetingDocuments: object[], chatInitParams: Record<string, unknown> }>}
 */
export async function fetchRoadshowEmbedKnowledgeBaseOptions({ roadshowId, title = '', t }) {
  const rid = String(roadshowId ?? '').trim();
  if (!rid) {
    return { knowledgeBaseOptions: [], meetingDocuments: [], chatInitParams: {} };
  }

  const chunkOpts = { hideMessage: true };

  try {
    const [summaryRes, docRes, liveRes, chunkRes] = await Promise.all([
      getSummaryInfo({ roadshowId: rid }),
      noticepagedocument({ roadshowid: rid }),
      attachmentlist({ roadshowid: rid }),
      checkDocChunk({ bizId: rid, pointsTypes: [0] }, chunkOpts)
    ]);

    const summaryData = summaryRes?.data ?? {};
    const docList = docRes?.data ?? [];
    const liveDocList = liveRes?.data ?? [];
    const docChunkExist = chunkRes?.data ?? [];

    const { meetSummary = {} } = summaryData;
    const isSyncSummary = docChunkExist[0];
    const displayTitle = String(title || '').trim() || rid;

    const result = [];

    // 如果有会议纪要(会议原文)
    if (meetSummary.releaseStatus === 1) {
      if (!isSyncSummary) {
        await uploadDocChunk(
          {
            uploadType: 1,
            summaryId: meetSummary.id,
            bizId: rid,
            summaryName: displayTitle,
            id: meetSummary.id,
            sourceType: 3
          },
          chunkOpts
        );
      }
      result.push({
        value: 'useSummary',
        checkedValue: true,
        defaultChecked: true,
        groupLabel: t('aiChat.current_summary'),
        label: displayTitle,
        prefix: 'icon-yejidianping',
        desc: t('summary.based_on_this_report'),
        callback: () => {}
      });
    }

    const documentList = mergeRoadshowDocArrays(
      docList.map((doc) => ({ ...doc, vectorType: 2 })),
      liveDocList.map((doc) => ({ ...doc, vectorType: 1 }))
    );

    let meetingDocuments = [];

    if (documentList.length > 0) {
      const formatDocList = await getRoadshowDocumentsVectorInfo(documentList);
      if (formatDocList?.length) {
        meetingDocuments = formatDocList;
        result.push({
          value: 'docBusinessIds',
          type: 'summaryDocFile',
          label: t('aiChat.roadshow_file'),
          prefix: 'icon-a-Property1yunwendang-weixuanzhong',
          desc: t('aiChat.this_roadshow_file'),
          callback: () => {},
          children: [
            { value: 'all', label: t('aiChat.all'), callback: () => {} },
            ...formatDocList.map((item) => ({
              value: `${item.id}`,
              label: item.docname ?? item.filename,
              disabled: item.embeddingStatus !== ROADSHOW_FILE_EMBEDDING_STATUS.SUCCESSED,
              embeddingStatus: item.embeddingStatus
            }))
          ]
        });
      }
    }

    // useSummary（会议原文）不再在此固定为 1，改由 chatSetting（用户勾选）驱动发送参数，
    // 避免取消勾选后仍被强制带上 useSummary=1。
    const chatInitParams = {};

    return { knowledgeBaseOptions: result, meetingDocuments, chatInitParams };
  } catch (e) {
    console.error('[fetchRoadshowEmbedKnowledgeBaseOptions]', e);
    return { knowledgeBaseOptions: [], meetingDocuments: [], chatInitParams: {} };
  }
}

class Bridge extends BridgeBase {
  constructor() {
    super(BRM_CHANNEL, false);

    /**
     * iframe 嵌入页 /thirdparty/externalAiChatIframe 中 customAiChat 初始 config；
     * 业务 ID 建议由 URL query：`businessId`（可选 `sourceType`，缺省为路演 ROADSHOW）。
     * 路演知识库选项不在此写死，由页面根据 businessId 调用 fetchRoadshowEmbedKnowledgeBaseOptions 合并。
     */
    this.jinbaoModules = {
      sourceType: AI_CHAT_SOURCE_TYPE.ROADSHOW,
      initAutoChat: false,
      disableSaveSetting: true,
      showChatContentStopButton: false,
      defaultWorkbenchWidth: 480,
      // 路演平台 iframe 嵌入场景下，历史会话点击「来源」不跳转外部路由，
      // 而是与点击会话标题一致：拉取并展示该会话详情
      openSourceAsSession: true,
      // 路演平台 iframe 嵌入场景下，隐藏表格 / 图表 / 图片 / 其他内容块的全屏功能
      hideContentFullScreen: true,
      delegateMemberToHost: true
    };
  }
}

export default new Bridge();

export { Bridge };
